import React, { Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../redux/hooks';
import widgetRegistry from '../widgets/widgetRegistry';
import { EditSaveButton, BackButton } from '../components';
import { useDynamicPageWidgets } from '../hooks/useDynamicPageWidgets';

interface DynamicPageProps {
  pageType: string;
  title?: string;
  backButtonText?: string;
  showBackButton?: boolean;
  showEditButton?: boolean;
}

const DynamicPage: React.FC<DynamicPageProps> = ({
  pageType,
  title,
  backButtonText = 'Back to Home',
  showBackButton = true,
  showEditButton = true
}) => {
  const navigate = useNavigate();
  const {isEditControl } = useAppSelector((state) => state?.editControl);

  // Use the dynamic page widgets hook
  const {
    widgets,
    updateWidgetData,
    toggleEditModeWithSave,
    isEditMode,
    isSaving,
    hasError,
    hasWidgets
  } = useDynamicPageWidgets(pageType);

  // Loading state when no widgets are found
  if (!hasWidgets) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-3 border-orange-500 border-t-transparent rounded-full mx-auto" />
          <p className="text-gray-600 font-medium mt-4">Loading {pageType}...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Error Loading {pageType}</h2>
          <p className="text-gray-600">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {/* Back Button and Title */}
      {showBackButton && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <BackButton text={backButtonText} onClick={() => navigate('/')} />
          {title && <h1 className="text-4xl font-bold mt-4">{title}</h1>}
        </div>
      )}

      {/* Dynamic Widget Rendering */}
      {widgets.map((widget, index) => {
        const { type, data } = widget;
        const Component = widgetRegistry[type];
        
        if (!Component) {
          console.warn(`Widget component not found for type: ${type}`);
          return (
            <div key={index} className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Widget Not Found</h2>
                <p className="text-gray-600">Type: {type || 'Unknown'}</p>
                <p className="text-gray-600 mt-2">Available: {Object.keys(widgetRegistry).join(', ')}</p>
              </div>
            </div>
          );
        }

        return (
          <Suspense 
            key={`${type}-${index}`}
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
              </div>
            }
          >
            <Component 
              data={data}
              isEditing={isEditMode}
              isEditMode={isEditMode}
              onUpdate={(newData: any) => updateWidgetData(index, newData)}
              onCardClick={(item: any) => console.log(`${pageType} item clicked:`, item)}
            />
          </Suspense>
        );
      })}

      {/* Edit Save Button */}
      {showEditButton && isEditControl && (
        <EditSaveButton
          isEditMode={isEditMode}
          onToggle={toggleEditModeWithSave}
          disabled={isSaving}
          variant="red"
          ariaLabel={isEditMode ? `Save ${pageType} Changes` : `Edit ${pageType}`}
        />
      )}
    </div>
  );
};

export default DynamicPage;
